import { z } from "zod";
import { type LearnerState, type Program, pageProgram, validateTree } from "./contract";

const ViewSchema = z
  .record(z.string().max(64), z.union([z.string().max(1000), z.number().finite(), z.boolean()]))
  .refine((v) => Object.keys(v).length <= 64);
export class CodeRuntime {
  private worker?: Worker;
  private serial = 0;
  private closed = false;
  async call(
    program: Program,
    state: LearnerState,
    load: boolean,
    action?: { id: string; value?: string | number | boolean }
  ) {
    if (this.closed) throw new Error("Runtime closed");
    if (!this.worker) {
      const { createWorker } = await import("./workerFactory");
      if (this.closed) throw new Error("Runtime closed");
      this.worker = createWorker();
    }
    const worker = this.worker;
    const id = ++this.serial;
    return new Promise<{ tree: ReturnType<typeof validateTree>; view: LearnerState["view"] }>(
      (resolve, reject) => {
        // Cold WASM/compiler loading gets a separate budget; ordinary execution does not.
        const timer = setTimeout(
          () => {
            this.close();
            reject(new Error("Runtime deadline exceeded"));
          },
          load ? 8000 : 500
        );
        worker.onerror = () => {
          clearTimeout(timer);
          this.close();
          reject(new Error("Exercise worker failed"));
        };
        worker.onmessage = ({ data }) => {
          if (data.id !== id) return;
          clearTimeout(timer);
          try {
            if (data.error) throw new Error(data.error);
            resolve({
              tree: validateTree(data.result.tree, pageProgram(program, state)),
              view: ViewSchema.parse(data.result.view),
            });
          } catch (e) {
            reject(e);
          }
        };
        worker.postMessage({
          id,
          type: load ? "load" : "run",
          program: load ? program : undefined,
          state,
          action,
        });
      }
    );
  }
  close() {
    this.closed = true;
    this.worker?.terminate();
  }
}
